import gsap from 'gsap'
import * as THREE from 'three'
import { MeshSurfaceSampler } from 'three/addons/math/MeshSurfaceSampler.js'
import { viewport, isMobile } from './core/viewport.js'
import { defaultParams } from './config/defaults.js'
import { params, saveParams } from './config/persistence.js'
import { createCamera } from './core/camera.js'
import { createRenderer } from './core/renderer.js'
import { initInput, updateInput, mouseWorld, smoothedMouseVelocity, suckAmount } from './core/input.js'
import { loadFont, createShapeGeometry, parsePLY, parseImage } from './simulation/geometry.js'
import { initCompute } from './simulation/compute.js'
import { initParticles, colorFromHex } from './rendering/particles.js'
import { initTrail } from './rendering/trail.js'
import { initControls } from './ui/controls.js'
import { createOverlay, isDebug } from './ui/overlay.js'

// --- Scene setup ---
const canvas = document.querySelector('canvas.viewport')
const scene = new THREE.Scene()

// --- Camera & controls ---
const { camera, controls } = createCamera(canvas, scene)

// --- Input handling ---
initInput(canvas, camera, controls)

// --- Renderer ---
const { renderer, composer, bloomPass } = createRenderer(canvas, scene, camera)

// --- Trail persistence ---
const { feedbackMaterial, outputMaterial, renderTargetA, renderTargetB, renderTrail } = initTrail()

// --- Loader UI ---
const loaderOverlay = document.createElement('div')
loaderOverlay.className = 'loader-overlay'
loaderOverlay.innerHTML = `
    <span class="loader-label">Loading</span>
    <div class="loader-track"><div class="loader-bar"></div></div>
    <span class="loader-size"></span>
`
document.body.appendChild(loaderOverlay)
const loaderBar = loaderOverlay.querySelector('.loader-bar')
const loaderSizeLabel = loaderOverlay.querySelector('.loader-size')

const formatMB = (bytes) => (bytes / (1024 * 1024)).toFixed(1) + ' MB'

const showLoader = (totalBytes) => {
    loaderBar.style.width = '0%'
    loaderSizeLabel.textContent = totalBytes ? formatMB(totalBytes) : ''
    loaderOverlay.classList.add('visible')
}

const updateLoader = (loaded, total) => {
    const pct = Math.round((loaded / total) * 100)
    loaderBar.style.width = pct + '%'
    loaderSizeLabel.textContent = `${formatMB(loaded)} / ${formatMB(total)}`
}

const hideLoader = () => {
    loaderOverlay.classList.remove('visible')
}

// --- Font ---
await loadFont()

// --- Scene state (mutable, rebuilt on preset switch) ---
let particleSystem = null
let compute = null

const disposeScene = () => {
    if (particleSystem) {
        scene.remove(particleSystem.points)
        particleSystem.geometry.dispose()
        particleSystem.material.dispose()
        particleSystem = null
    }
    if (window._splatCloud) {
        scene.remove(window._splatCloud)
        window._splatCloud.geometry.dispose()
        window._splatMaterial.dispose()
        window._splatCloud = null
        window._splatMaterial = null
    }
    compute = null
}

const buildScene = async () => {
    const particleCount = params.particleCount
    const sampledPositions = new Float32Array(particleCount * 3)
    const tempPosition = new THREE.Vector3()
    let plyColorData = null

    if (params.shape === 'ply' && params.plyFile) {
        showLoader(0)
        const plyData = await parsePLY(params.plyFile, (loaded, total) => {
            updateLoader(loaded, total)
        })
        hideLoader()

        const validIndices = []
        for (let i = 0; i < plyData.vertexCount; i++) {
            if (plyData.opacities[i] > 0.1) validIndices.push(i)
        }

        let minX = Infinity, minY = Infinity, minZ = Infinity
        let maxX = -Infinity, maxY = -Infinity, maxZ = -Infinity
        for (const idx of validIndices) {
            const x = plyData.positions[idx * 3 + 0]
            const y = plyData.positions[idx * 3 + 1]
            const z = plyData.positions[idx * 3 + 2]
            minX = Math.min(minX, x); maxX = Math.max(maxX, x)
            minY = Math.min(minY, y); maxY = Math.max(maxY, y)
            minZ = Math.min(minZ, z); maxZ = Math.max(maxZ, z)
        }
        const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2, cz = (minZ + maxZ) / 2
        const extent = Math.max(maxX - minX, maxY - minY, maxZ - minZ)
        const mobileScale = isMobile ? 0.6 : 1.0
        const scale = (8.0 * params.shapeScale * mobileScale) / extent
        const depthScale = 2.0

        const splatCount = validIndices.length
        const splatPositions = new Float32Array(splatCount * 3)
        const splatColors = new Float32Array(splatCount * 4)
        for (let i = 0; i < splatCount; i++) {
            const idx = validIndices[i]
            splatPositions[i * 3 + 0] = (plyData.positions[idx * 3 + 0] - cx) * scale
            splatPositions[i * 3 + 1] = -(plyData.positions[idx * 3 + 1] - cy) * scale
            splatPositions[i * 3 + 2] = (plyData.positions[idx * 3 + 2] - cz) * scale * depthScale
            splatColors[i * 4 + 0] = plyData.colors[idx * 3 + 0]
            splatColors[i * 4 + 1] = plyData.colors[idx * 3 + 1]
            splatColors[i * 4 + 2] = plyData.colors[idx * 3 + 2]
            splatColors[i * 4 + 3] = plyData.opacities[idx]
        }

        let scx = 0, scy = 0, scz = 0
        for (let i = 0; i < splatCount; i++) {
            scx += splatPositions[i * 3 + 0]
            scy += splatPositions[i * 3 + 1]
            scz += splatPositions[i * 3 + 2]
        }
        scx /= splatCount; scy /= splatCount; scz /= splatCount
        for (let i = 0; i < splatCount; i++) {
            splatPositions[i * 3 + 0] -= scx
            splatPositions[i * 3 + 1] -= scy
            splatPositions[i * 3 + 2] -= scz
        }

        const splatGeometry = new THREE.BufferGeometry()
        splatGeometry.setAttribute('position', new THREE.BufferAttribute(splatPositions, 3))
        splatGeometry.setAttribute('aColor', new THREE.BufferAttribute(splatColors, 4))
        const splatMaterial = new THREE.ShaderMaterial({
            vertexShader: /*glsl*/`
                attribute vec4 aColor;
                varying vec4 vColor;
                varying vec3 vWorldPos;
                uniform float uPointSize;
                uniform vec2 uResolution;
                void main() {
                    vColor = aColor;
                    vec4 worldPos = modelMatrix * vec4(position, 1.0);
                    vWorldPos = worldPos.xyz;
                    vec4 mvPos = viewMatrix * worldPos;
                    gl_Position = projectionMatrix * mvPos;
                    gl_PointSize = uPointSize * uResolution.y * (1.0 / -mvPos.z);
                }
            `,
            fragmentShader: /*glsl*/`
                varying vec4 vColor;
                varying vec3 vWorldPos;
                uniform vec3 uMouseWorld;
                uniform float uRevealRadius;
                uniform float uRevealSoftness;
                void main() {
                    float d = length(gl_PointCoord - 0.5);
                    if(d > 0.5) discard;
                    float alpha = vColor.a * (1.0 - smoothstep(0.2, 0.5, d));
                    if(uRevealRadius > 0.0) {
                        float mouseDist = length(vWorldPos.xy - uMouseWorld.xy);
                        float hide = smoothstep(uRevealRadius * (1.0 - uRevealSoftness), uRevealRadius, mouseDist);
                        alpha *= hide;
                    }
                    gl_FragColor = vec4(vColor.rgb, alpha);
                }
            `,
            transparent: true,
            depthWrite: false,
            blending: THREE.NormalBlending,
            uniforms: {
                uPointSize: { value: 0.035 },
                uResolution: { value: new THREE.Vector2(viewport.width * viewport.pixelRatio, viewport.height * viewport.pixelRatio) },
                uMouseWorld: { value: new THREE.Vector3(9999, 9999, 9999) },
                uRevealRadius: { value: params.revealEnabled ? params.revealRadius : 0.0 },
                uRevealSoftness: { value: params.revealSoftness },
            },
        })
        const splatCloud = new THREE.Points(splatGeometry, splatMaterial)
        splatCloud.renderOrder = -1
        splatCloud.visible = params.revealEnabled
        scene.add(splatCloud)

        window._splatMaterial = splatMaterial
        window._splatCloud = splatCloud

        const brightIndices = validIndices.filter(idx => {
            const r = plyData.colors[idx * 3 + 0]
            const g = plyData.colors[idx * 3 + 1]
            const b = plyData.colors[idx * 3 + 2]
            return (r * 0.299 + g * 0.587 + b * 0.114) > 0.08
        })
        const sourceIndices = brightIndices.length > 0 ? brightIndices : validIndices

        plyColorData = new Float32Array(particleCount * 3)
        for (let i = 0; i < particleCount; i++) {
            const srcIdx = sourceIndices[Math.floor(Math.random() * sourceIndices.length)]
            sampledPositions[i * 3 + 0] = (plyData.positions[srcIdx * 3 + 0] - cx) * scale - scx
            sampledPositions[i * 3 + 1] = -(plyData.positions[srcIdx * 3 + 1] - cy) * scale - scy
            sampledPositions[i * 3 + 2] = (plyData.positions[srcIdx * 3 + 2] - cz) * scale * depthScale - scz
            plyColorData[i * 3 + 0] = plyData.colors[srcIdx * 3 + 0]
            plyColorData[i * 3 + 1] = plyData.colors[srcIdx * 3 + 1]
            plyColorData[i * 3 + 2] = plyData.colors[srcIdx * 3 + 2]
        }
    } else if (params.shape === 'image' && params.imageFile) {
        const imgData = await parseImage(params.imageFile)

        const scale = 8.0 * params.shapeScale

        plyColorData = new Float32Array(particleCount * 3)
        for (let i = 0; i < particleCount; i++) {
            const srcIdx = Math.floor(Math.random() * imgData.vertexCount)
            sampledPositions[i * 3 + 0] = imgData.positions[srcIdx * 3 + 0] * scale
            sampledPositions[i * 3 + 1] = imgData.positions[srcIdx * 3 + 1] * scale
            sampledPositions[i * 3 + 2] = (Math.random() - 0.5) * 0.2 * scale
            plyColorData[i * 3 + 0] = imgData.colors[srcIdx * 3 + 0]
            plyColorData[i * 3 + 1] = imgData.colors[srcIdx * 3 + 1]
            plyColorData[i * 3 + 2] = imgData.colors[srcIdx * 3 + 2]
        }
    } else {
        const sourceGeometry = createShapeGeometry(params.shape, params)
        const sourceMesh = new THREE.Mesh(sourceGeometry, new THREE.MeshBasicMaterial())
        const sampler = new MeshSurfaceSampler(sourceMesh).build()

        for (let i = 0; i < particleCount; i++) {
            sampler.sample(tempPosition)
            sampledPositions[i * 3 + 0] = tempPosition.x
            sampledPositions[i * 3 + 1] = tempPosition.y
            sampledPositions[i * 3 + 2] = tempPosition.z
        }
    }

    // --- GPU computation ---
    const { compute: newCompute } = initCompute(renderer, sampledPositions, particleCount)
    compute = newCompute

    // --- Particle system ---
    particleSystem = initParticles(scene, compute, particleCount, plyColorData)

    return { particleSystem, compute }
}

// --- Fade animation system (GSAP) ---
const fadeState = { value: 0.0 }

window._animateFade = (target, direction) => {
    return new Promise(resolve => {
        gsap.killTweensOf(fadeState)
        gsap.to(fadeState, {
            value: target,
            duration: direction === 'out' ? 1.0 : direction === 'in' ? 2.5 : 1.0,
            ease: direction === 'out' ? 'power1.in' : 'power1.out',
            onComplete: resolve,
        })
    })
}

// --- Initial build ---
await buildScene()

// --- Overlay UI ---
createOverlay()

// In debug mode, overlay skips — fade in directly
if (isDebug) window._animateFade(1.0)

// --- UI controls (debug mode only) ---
let fpsGraph = null
if (isDebug) {
    const ui = initControls({
        particleSystem,
        compute,
        controls,
        renderer,
        bloomPass,
        trailFeedbackMaterial: feedbackMaterial,
        trailOutputMaterial: outputMaterial,
        trailRenderTargetA: renderTargetA,
        trailRenderTargetB: renderTargetB,
    })
    fpsGraph = ui.fpsGraph
}
window._applyPresetParams = () => {
    if (!particleSystem || !compute) return
    renderer.setClearColor(params.backgroundColor)
    particleSystem.material.uniforms.uSize.value = params.particleSize
    particleSystem.material.uniforms.uColor.value = colorFromHex(params.particleColor)
    particleSystem.material.uniforms.uColorShift.value = colorFromHex(params.colorShift)
    particleSystem.material.uniforms.uColorVariance.value = params.colorVariance
    particleSystem.material.uniforms.uOpacityVariance.value = params.opacityVariance
    particleSystem.material.uniforms.uSizeVariance.value = params.sizeVariance
    particleSystem.material.uniforms.uFlashIntensity.value = params.flashIntensity
    particleSystem.material.uniforms.uDimIntensity.value = params.dimIntensity
    particleSystem.material.uniforms.uSaturation.value = params.saturation !== undefined ? params.saturation : 1.0
    particleSystem.material.uniforms.uParticleShape.value = params.particleShape
    particleSystem.material.uniforms.uShapeSoftness.value = params.shapeSoftness
    particleSystem.material.uniforms.uDofFocus.value = params.dofEnabled ? params.dofFocus : 0.0
    particleSystem.material.uniforms.uDofAperture.value = params.dofEnabled ? params.dofAperture : 0.0
    particleSystem.material.uniforms.uDofMaxBlur.value = params.dofEnabled ? params.dofMaxBlur : 0.0
    const revealR = params.revealEnabled ? params.revealRadius : 0.0
    particleSystem.material.uniforms.uRevealRadius.value = revealR
    particleSystem.material.uniforms.uRevealSoftness.value = params.revealSoftness
    if (window._splatMaterial) {
        window._splatMaterial.uniforms.uRevealRadius.value = revealR
        window._splatMaterial.uniforms.uRevealSoftness.value = params.revealSoftness
    }
    if (window._splatCloud) window._splatCloud.visible = params.revealEnabled
    compute.velocityVariable.material.uniforms.uFlowFieldInfluence.value = params.flowInfluence
    compute.velocityVariable.material.uniforms.uFlowFieldStrength.value = params.flowStrength
    compute.velocityVariable.material.uniforms.uFlowFieldFrequency.value = params.flowFrequency
    compute.particlesVariable.material.uniforms.uMouseRadius.value = params.mouseRadius
    compute.velocityVariable.material.uniforms.uMouseRadius.value = params.mouseRadius
    compute.particlesVariable.material.uniforms.uMouseAspect.value = params.mouseAspect
    compute.velocityVariable.material.uniforms.uMouseAspect.value = params.mouseAspect
    compute.velocityVariable.material.uniforms.uSuckStrength.value = params.suckStrength
    compute.velocityVariable.material.uniforms.uWindStrength.value = params.windStrength
    compute.particlesVariable.material.uniforms.uMouseTrail.value = params.mouseTrail
    compute.particlesVariable.material.uniforms.uDecayRate.value = params.respawnRate
    compute.velocityVariable.material.uniforms.uDamping.value = params.damping
    compute.velocityVariable.material.uniforms.uTurbulenceStrength.value = params.turbulenceStrength
    compute.velocityVariable.material.uniforms.uTurbulenceFrequency.value = params.turbulenceFrequency
    compute.velocityVariable.material.uniforms.uMorphStrength.value = params.morphEnabled ? params.morphStrength : 0
    controls.enableRotate = params.enableRotate
    controls.enableZoom = params.enableZoom
    controls.enablePan = params.enablePan
    controls.autoRotate = params.autoRotate
    controls.autoRotateSpeed = params.autoRotateSpeed
    camera.position.set(0, 0, 18)
    controls.target.set(0, 0, 0)
    controls.update()
    bloomPass.strength = params.bloomStrength
    bloomPass.radius = params.bloomRadius
    bloomPass.threshold = params.bloomThreshold
    renderer.toneMapping = params.toneMapping
    renderer.toneMappingExposure = params.exposure
    feedbackMaterial.uniforms.trailAmount.value = params.trailAmount / 100
    feedbackMaterial.uniforms.blurAmount.value = params.trailBlurAmount
    feedbackMaterial.uniforms.brightPersist.value = params.trailBrightPersist
    feedbackMaterial.uniforms.blendMode.value = params.trailBlendMode
    outputMaterial.uniforms.trailAmount.value = params.trailAmount / 100
    outputMaterial.uniforms.brightPersist.value = params.trailBrightPersist
    outputMaterial.uniforms.blendMode.value = params.trailBlendMode
}

window._rebuildScene = async () => {
    disposeScene()
    await buildScene()
}

// --- Auto-rotate toggle listener + smooth icon spin ---
let rotateIconAngle = 0
let rotateIconSpeed = 0
const rotateIconTargetSpeed = 90 // degrees per second
const rotateSvg = () => document.querySelector('.rotate-toggle svg')

window.addEventListener('toggle-rotate', (e) => {
    controls.autoRotate = e.detail
    controls.autoRotateSpeed = params.autoRotateSpeed || 2.0
})

// --- Render loop ---
const clock = new THREE.Clock()
let previousTime = 0

const frame = () => {
    if (fpsGraph) fpsGraph.begin()

    const elapsedTime = clock.getElapsedTime()
    const deltaTime = Math.min(elapsedTime - previousTime, 0.05)
    previousTime = elapsedTime

    controls.update(deltaTime)
    updateInput(camera, deltaTime, params)

    // Smooth rotate icon spin
    const targetSpeed = params.autoRotate ? rotateIconTargetSpeed : 0
    rotateIconSpeed += (targetSpeed - rotateIconSpeed) * Math.min(deltaTime * 3.0, 1.0)
    if (Math.abs(rotateIconSpeed) > 0.1) {
        rotateIconAngle = (rotateIconAngle + rotateIconSpeed * deltaTime) % 360
        const svg = rotateSvg()
        if (svg) svg.style.transform = `rotate(${rotateIconAngle}deg)`
    }

    if (!particleSystem || !compute) {
        if (fpsGraph) fpsGraph.end()
        window.requestAnimationFrame(frame)
        return
    }

    // GPGPU uniforms
    const pU = compute.particlesVariable.material.uniforms
    const vU = compute.velocityVariable.material.uniforms

    pU.uTime.value = elapsedTime
    pU.uDeltaTime.value = deltaTime
    pU.uMouse.value.copy(mouseWorld)
    pU.uSuckAmount.value = suckAmount

    vU.uTime.value = elapsedTime
    vU.uDeltaTime.value = deltaTime
    vU.uMouse.value.copy(mouseWorld)
    vU.uSuckAmount.value = suckAmount
    vU.uMouseVelocity.value.copy(smoothedMouseVelocity)

    if (params.morphEnabled) {
        const morphAmount = (Math.sin(elapsedTime * params.morphSpeed) + 1.0) * 0.5
        pU.uMorphAmount.value = morphAmount
        vU.uMorphAmount.value = morphAmount
        vU.uMorphStrength.value = params.morphStrength
    } else {
        pU.uMorphAmount.value = 0
        vU.uMorphAmount.value = 0
        vU.uMorphStrength.value = 0
    }

    compute.computation.compute()
    particleSystem.material.uniforms.uParticlesTexture.value = compute.computation.getCurrentRenderTarget(compute.particlesVariable).texture
    particleSystem.material.uniforms.uTime.value = elapsedTime
    particleSystem.material.uniforms.uMouseWorld.value.copy(mouseWorld)
    particleSystem.material.uniforms.uGlobalFade.value = fadeState.value
    if (window._splatMaterial)
        window._splatMaterial.uniforms.uMouseWorld.value.copy(mouseWorld)

    bloomPass.enabled = params.bloomEnabled

    if (params.trailEnabled) {
        feedbackMaterial.uniforms.uDeltaTime.value = deltaTime
        outputMaterial.uniforms.uDeltaTime.value = deltaTime
        renderTrail(renderer, composer, params.bloomEnabled)
    } else if (params.bloomEnabled) {
        composer.renderToScreen = true
        composer.render()
    } else {
        renderer.render(scene, camera)
    }

    if (fpsGraph) fpsGraph.end()
    window.requestAnimationFrame(frame)
}

frame()
